// src/review/canvas/navigation/ThreatNavigator.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Stack, Card, Text, ScrollArea, UnstyledButton, Group, Badge, ActionIcon, Tooltip } from '@mantine/core';
import { IconCheck, IconArrowLeft } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { useReview } from '../../context/ReviewContext';

/**
 * ThreatNavigator - Left sidebar for navigating through security threats
 * Shows all threats grouped by category with review status
 */
function ThreatNavigator({ threatsByCategory, currentThreat, onThreatSelect }) {
  const { modelId, goToStep } = useReview();

  // Get reviewed threats from Redux
  const reviewState = useSelector((state) =>
    state.reviews.byModel[modelId] || {}
  );
  const reviewedThreats = reviewState.threatsReviewed || [];

  return (
    <Card withBorder p="md" style={{ height: '100%' }}>
      <Stack gap="md">
        {/* Header with THREAT CATEGORIES title and Back to Risks icon */}
        <Group justify="space-between" align="center">
          <Text size="sm" fw={600} c="dimmed">THREAT CATEGORIES</Text>
          <Tooltip label="Back to Risks">
            <ActionIcon
              variant="light"
              color="blue"
              size="md"
              onClick={() => goToStep('risks')}
            >
              <IconArrowLeft size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <ScrollArea style={{ height: 'calc(100vh - 200px)' }}>
          <Stack gap="lg">
            {Object.entries(threatsByCategory).map(([categoryName, threats]) => (
              <Stack key={categoryName} gap="xs">
                <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                  {categoryName}
                </Text>

                {threats.map((threat) => {
                  const isActive = currentThreat?.threatId === threat.threatId;
                  const isReviewed = reviewedThreats.includes(threat.threatId);

                  return (
                    <UnstyledButton
                      key={threat.threatId}
                      onClick={() => onThreatSelect(threat)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 4,
                        backgroundColor: isActive ? '#f3f0ff' : 'transparent',
                        border: isActive ? '1px solid #7c3aed' : '1px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Text
                          size="sm"
                          fw={isActive ? 600 : 400}
                          c={isActive ? 'violet' : 'dark'}
                          lineClamp={2}
                          style={{ flex: 1 }}
                        >
                          {threat.threatScenario || threat.threatId}
                        </Text>

                        {isReviewed && (
                          <Badge
                            size="xs"
                            variant="filled"
                            color="green"
                            leftSection={<IconCheck size={10} />}
                            style={{ minWidth: 60 }}
                          >
                            Reviewed
                          </Badge>
                        )}
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </Stack>
            ))}
          </Stack>
        </ScrollArea>
      </Stack>
    </Card>
  );
}

ThreatNavigator.propTypes = {
  threatsByCategory: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        threatId: PropTypes.string.isRequired,
        threatScenario: PropTypes.string,
        category: PropTypes.string,
      })
    )
  ).isRequired,
  currentThreat: PropTypes.shape({
    threatId: PropTypes.string.isRequired,
    threatScenario: PropTypes.string,
  }),
  onThreatSelect: PropTypes.func.isRequired,
};

ThreatNavigator.defaultProps = {
  currentThreat: null,
};

export default ThreatNavigator;
